
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import CategoriesTable from "@/components/admin/categories/CategoriesTable";
import CategoryForm from "@/components/admin/categories/CategoryForm";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const AdminCategories = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const { data: categories, isLoading, refetch } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select(`
          *,
          parent:categories!parent_id(name)
        `)
        .order('name');
      
      if (error) throw error;

      // Get children count for each category
      const categoriesWithChildren = await Promise.all(
        data.map(async (category) => {
          const { data: children, error: childrenError } = await supabase
            .from('categories')
            .select('*')
            .eq('parent_id', category.id);
          
          if (childrenError) throw childrenError;
          
          return {
            ...category,
            children: children || []
          };
        })
      );

      return categoriesWithChildren;
    },
  });

  const handleCategorySaved = () => {
    refetch();
    setIsCreateDialogOpen(false);
    setEditingCategory(null);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-admin-foreground">Category Management</h1>
            <p className="text-admin-muted-foreground mt-1">
              Organize your products with categories and subcategories
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-admin-accent-blue hover:bg-admin-accent-blue/90">
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Category</DialogTitle>
              </DialogHeader>
              <CategoryForm
                categories={categories || []}
                onSaved={handleCategorySaved}
                onCancel={() => setIsCreateDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>

        <CategoriesTable
          categories={categories || []}
          isLoading={isLoading}
          onEdit={setEditingCategory}
          onRefresh={refetch}
        />

        {editingCategory && (
          <Dialog open={!!editingCategory} onOpenChange={() => setEditingCategory(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Category</DialogTitle>
              </DialogHeader>
              <CategoryForm
                category={editingCategory}
                categories={categories || []}
                onSaved={handleCategorySaved}
                onCancel={() => setEditingCategory(null)}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCategories;
